import { createDateFormatter } from "@nexus/ui";
import {
  categoryNoDataAreas,
  CHART_LEGEND_ICONS,
  escapeHtml,
  missingDataMode,
  NO_DATA_LABEL,
  periodBounds,
  periodMonths,
  positionChartTooltip,
  trimMissing,
  useChartLifecycle,
  type MissingDataMode,
} from "@nexus/widget-kit";
export { escapeHtml } from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, type BarSeriesOption } from "echarts/charts";
import { LegacyGridContainLabel } from "echarts/features";
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type MarkAreaComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetState,
  MetricStat,
  withAlpha,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CATEGORY_SERIES_CONTRACT } from "./types";
import type { CategoryPoint, CategorySeriesData } from "./types";

echarts.use([
  BarChart,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  CanvasRenderer,
  LegacyGridContainLabel,
]);

type StackedOption = echarts.ComposeOption<
  | BarSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | MarkAreaComponentOption
  | TooltipComponentOption
>;

export interface StackedCategoryBarsConfig {
  title?: string;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
  /** Shows only this tenant's rows; without it every tenant of the binding is added up. */
  tenantId?: number;
  /** Categories beyond this many (largest first) are grouped as "Other". */
  maxCategories?: number;
  /** Adds the summary strip and the data note around the chart. */
  showDetails?: boolean;
  /** Show gap (default) keeps every month of the period; Fit to data crops the months without data at its ends. */
  missingData?: MissingDataMode;
}

export const LABELS = defineLabels([
  { key: "yAxis", name: "Value axis title", default: "Network IDs" },
  {
    key: "other",
    name: "Grouped categories",
    default: "Other",
    hint: "The legend entry for the smallest categories, grouped when there are too many to read.",
  },
  { key: "total", name: "Total (tooltip)", default: "Total", advanced: true },
  { key: "summaryLatest", name: "Summary: latest period", default: "Latest month", advanced: true },
  {
    key: "summaryTop",
    name: "Summary: largest category",
    default: "Largest category",
    advanced: true,
  },
  { key: "summaryChange", name: "Summary: change", default: "Change vs previous", advanced: true },
  { key: "emptyTitle", name: "Empty title", default: "No data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "Nothing was reported for this period.",
  },
] as const);

export type StackedLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;
const DEFAULT_LABELS: StackedLabels = resolveLabels(LABELS, undefined);

export const manifest: WidgetManifest = {
  id: "stacked-category-bars",
  name: "Stacked Category Bars",
  description:
    "Monthly counts stacked by category, for one tenant or all the tenants of the report.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    series: {
      source: "network-id",
      contract: CATEGORY_SERIES_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: StackedCategoryBarsConfig = {
  title: "Network ID",
  maxCategories: 6,
  showDetails: false,
};

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };
const DEFAULT_MAX_CATEGORIES = 6;

// ── Formatting ────────────────────────────────────────────────────────────────

const countFormat = new Intl.NumberFormat("en");
const compactFormat = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const monthFormat = createDateFormatter(
  "utc",
  {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  },
  "en",
);

export function formatCount(value: number): string {
  return countFormat.format(value);
}

/** `YYYY-MM` as "Sep 2026"; any other bucket is shown as received. */
export function formatBucket(bucket: string): string {
  if (!/^\d{4}-\d{2}$/.test(bucket)) return bucket;
  const date = new Date(`${bucket}-01T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? bucket : monthFormat.format(date);
}

// ── Model ─────────────────────────────────────────────────────────────────────

export interface StackedModel {
  buckets: string[];
  /** Largest first, "Other" (when present) last. Stable across buckets. */
  categories: { key: string; label: string; grouped: boolean }[];
  /** `values[categoryIndex][bucketIndex]`, zero where the category has no row. */
  values: number[][];
  totals: number[];
  /** Buckets without any row: no data, never a zero month. */
  missing: boolean[];
}

/**
 * Rows to stacks. Categories are ranked by their total over the whole window, so
 * their order (and color) never changes from one bucket to the next, and a
 * category missing from a bucket counts as zero. The smallest ones are grouped.
 */
export function buildModel(
  data: CategorySeriesData,
  options?: {
    tenantId?: number;
    maxCategories?: number;
    otherLabel?: string;
    missingData?: MissingDataMode;
  },
): StackedModel {
  const rows: CategoryPoint[] = data.points.filter(
    (point) =>
      (options?.tenantId === undefined || point.tenantId === options.tenantId) && point.count > 0,
  );
  const measured = new Set(rows.map((row) => row.bucket));
  const period = periodBounds(data.period);
  const months = [...new Set([...(period ? periodMonths(period) : []), ...measured])].sort();
  const buckets =
    options?.missingData === "fit"
      ? trimMissing(months, (bucket) => !measured.has(bucket))
      : months;
  const totalByCategory = new Map<string, number>();
  for (const row of rows)
    totalByCategory.set(row.category, (totalByCategory.get(row.category) ?? 0) + row.count);
  const ranked = [...totalByCategory.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => key);
  const max = Math.max(1, options?.maxCategories ?? DEFAULT_MAX_CATEGORIES);
  // Grouping only pays off when it hides at least two categories.
  const grouping = ranked.length > max + 1;
  const kept = grouping ? ranked.slice(0, max) : ranked;
  const keptSet = new Set(kept);

  const categories = kept.map((key) => ({ key, label: key, grouped: false }));
  if (grouping)
    categories.push({ key: "\u0000other", label: options?.otherLabel ?? "Other", grouped: true });

  const index = new Map(categories.map((category, position) => [category.key, position]));
  const bucketIndex = new Map(buckets.map((bucket, position) => [bucket, position]));
  const values = categories.map(() => buckets.map(() => 0));
  for (const row of rows) {
    const category = index.get(keptSet.has(row.category) ? row.category : "\u0000other");
    const bucket = bucketIndex.get(row.bucket);
    if (category === undefined || bucket === undefined) continue;
    values[category][bucket] += row.count;
  }
  const totals = buckets.map((_, position) =>
    values.reduce((sum, series) => sum + series[position], 0),
  );
  return { buckets, categories, values, totals, missing: buckets.map((b) => !measured.has(b)) };
}

export interface StackedSummary {
  latest: { bucket: string; total: number } | null;
  /** Largest category of the latest bucket. */
  top: { label: string; count: number } | null;
  /** Relative change of the total against the previous bucket, `null` without one. */
  change: number | null;
}

export function deriveSummary(model: StackedModel): StackedSummary {
  // The latest month with data; a trailing month without data says nothing.
  const last = model.missing.lastIndexOf(false);
  if (last < 0) return { latest: null, top: null, change: null };
  let top: StackedSummary["top"] = null;
  model.categories.forEach((category, position) => {
    const count = model.values[position][last];
    if (count > 0 && !category.grouped && (!top || count > top.count))
      top = { label: category.label, count };
  });
  const previous = last > 0 ? model.totals[last - 1] : 0;
  return {
    latest: { bucket: model.buckets[last], total: model.totals[last] },
    top,
    change: previous > 0 ? (model.totals[last] - previous) / previous : null,
  };
}

export function describeChart(model: StackedModel): string {
  const summary = deriveSummary(model);
  if (!summary.latest) return "No data.";
  const top = summary.top ? `, largest category ${summary.top.label}` : "";
  return `Monthly counts by category over ${model.buckets.length} months, ${model.categories.length} categories. ${formatBucket(summary.latest.bucket)}: ${formatCount(summary.latest.total)}${top}.`;
}

// ── Chart option ──────────────────────────────────────────────────────────────

export function categoryColor(colors: ChartColors, position: number, grouped: boolean): string {
  // "Other" is muted so it never competes with a named category.
  return grouped ? withAlpha(colors.label, 0.45) : colors.series[position % colors.series.length];
}

export function buildOption(
  model: StackedModel,
  colors: ChartColors,
  options?: { labels?: StackedLabels },
): StackedOption {
  const labels = options?.labels ?? DEFAULT_LABELS;
  return {
    animationDuration: 400,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    grid: { top: 48, right: 24, bottom: 8, left: 36, containLabel: true },
    legend: {
      top: 4,
      left: "center",
      type: "scroll",
      data: model.categories.map((category) => category.label),
      icon: CHART_LEGEND_ICONS.rect,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 16,
      textStyle: { color: colors.label, fontSize: colors.typography?.labelSize ?? 12, ...fontBase },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisPointer: { type: "shadow", shadowStyle: { color: withAlpha(colors.label, 0.08) } },
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesName?: string;
          marker?: string;
          dataIndex?: number;
          value?: number;
        }>;
        const position = items[0]?.dataIndex;
        if (typeof position !== "number") return "";
        const heading = `<div style="margin-bottom:4px;opacity:0.75">${escapeHtml(formatBucket(model.buckets[position]))}</div>`;
        if (model.missing[position]) return `${heading}<div>${NO_DATA_LABEL}</div>`;
        // Categories with nothing that month are left out rather than listed as zero.
        const rows = items
          .filter((item) => item.seriesName && (item.value ?? 0) > 0)
          .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
          .map(
            (item) =>
              `<div style="display:flex;justify-content:space-between;gap:16px"><span>${item.marker ?? ""}${escapeHtml(item.seriesName ?? "")}</span><b>${formatCount(item.value ?? 0)}</b></div>`,
          )
          .join("");
        return `${heading}${rows}<div style="display:flex;justify-content:space-between;gap:16px;margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.label, 0.25)}"><span>${escapeHtml(labels.total)}</span><b>${formatCount(model.totals[position])}</b></div>`;
      },
    },
    xAxis: {
      type: "category",
      data: model.buckets,
      axisLabel: {
        color: colors.label,
        hideOverlap: true,
        margin: 10,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
        formatter: (value: string) => formatBucket(value),
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.axisLine } },
    },
    yAxis: {
      type: "value",
      min: 0,
      name: labels.yAxis,
      nameLocation: "middle",
      nameRotate: 90,
      nameGap: 46,
      nameTextStyle: {
        color: withAlpha(colors.label, 0.7),
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: colors.grid, type: "solid" } },
      axisLabel: {
        color: colors.label,
        fontSize: colors.typography?.labelSize ?? 12,
        formatter: (value: number) => compactFormat.format(value),
        ...fontBase,
      },
    },
    series: model.categories.map((category, position) => {
      const color = categoryColor(colors, position, category.grouped);
      return {
        name: category.label,
        type: "bar",
        stack: "total",
        data: model.values[position].map((value, bucket) => (model.missing[bucket] ? null : value)),
        barMaxWidth: 56,
        barCategoryGap: "26%",
        // A hairline in the card color keeps neighbouring segments distinct.
        itemStyle: { color, borderColor: colors.tooltipBg, borderWidth: 1 },
        emphasis: { focus: "series" },
        ...(position === 0
          ? {
              markArea: {
                silent: true,
                data: categoryNoDataAreas(model.buckets, model.missing, colors),
              },
            }
          : {}),
      } satisfies BarSeriesOption;
    }),
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function SummaryStrip({ summary, labels }: { summary: StackedSummary; labels: StackedLabels }) {
  const { latest, top, change } = summary;
  return (
    <dl className="grid shrink-0 grid-cols-1 gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-(--density-widget-body-padding) @sm/widget:grid-cols-2 @3xl/widget:grid-cols-3">
      <MetricStat
        label={labels.summaryLatest}
        value={latest ? formatCount(latest.total) : "—"}
        detail={latest ? formatBucket(latest.bucket) : undefined}
      />
      <MetricStat
        label={labels.summaryTop}
        value={top ? top.label : "—"}
        detail={top ? formatCount(top.count) : undefined}
      />
      <MetricStat
        label={labels.summaryChange}
        value={change !== null ? `${change > 0 ? "+" : ""}${(change * 100).toFixed(1)}%` : "—"}
      />
    </dl>
  );
}

function StackedCategoryBarsComponent({ data, config }: WidgetProps<StackedCategoryBarsConfig>) {
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);

  const error = firstError(data, ["series"]);
  const series = unwrap<CategorySeriesData>(data.series);
  const isLoading = data.series?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const showDetails = config.showDetails ?? false;
  const missingData = missingDataMode(config.missingData);
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as StackedLabels, [labelsJson]);

  const model = useMemo(
    () =>
      series
        ? buildModel(series, {
            tenantId: config.tenantId,
            maxCategories: config.maxCategories,
            otherLabel: labels.other,
            missingData,
          })
        : null,
    [series, config.tenantId, config.maxCategories, labels.other, missingData],
  );
  const summary = useMemo(() => (model ? deriveSummary(model) : null), [model]);
  const isEmpty = !isLoading && !error && (!model || model.missing.every(Boolean));
  const showChart = !error && !isEmpty && model !== null;
  const firstLoad = isLoading && !hasRenderedRef.current;

  const modelRef = useRef(model);
  const labelsRef = useRef(labels);
  useEffect(() => {
    modelRef.current = model;
    labelsRef.current = labels;
  }, [model, labels]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading || !model) return;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
    }
    // `notMerge`: categories change with the data, so a stale series must not stay drawn.
    chartInstanceRef.current.setOption(
      buildOption(model, resolveChartColors(host), { labels }),
      true,
    );
    hasRenderedRef.current = true;
    markContentReady();
  }, [model, labels, error, isEmpty, isLoading, markContentReady]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    const current = modelRef.current;
    if (hasRenderedRef.current && current) {
      chart.setOption(
        buildOption(current, resolveChartColors(host), { labels: labelsRef.current }),
        true,
      );
    }
  });

  const freshness = series?.freshness;
  const note = [
    "Monthly counts",
    freshness ? `data through ${formatBucket(freshness.dataThrough.slice(0, 7))}` : null,
    freshness?.isEstimated ? "Estimated" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Widget
      title={title}
      intrinsicHeight={!inFocusOverlay}
      actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}
    >
      <div
        className={`flex w-full flex-col ${inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {summary && showChart && showDetails && <SummaryStrip summary={summary} labels={labels} />}
        <div className="relative min-h-(--widget-chart-min-height) w-full flex-1">
          <div
            ref={chartRef}
            role="img"
            aria-label={model ? describeChart(model) : "Stacked category chart"}
            className="absolute inset-0 h-full w-full transition-opacity duration-200"
            style={{ opacity: showChart ? 1 : 0 }}
          />
          {error && <WidgetIssueState error={error} className="absolute inset-0" />}
          {isEmpty && (
            <WidgetState
              title={labels.emptyTitle}
              description={labels.emptyDescription}
              className="absolute inset-0"
            />
          )}
          {firstLoad && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
        </div>
        {showChart && showDetails && (
          <p className="shrink-0 px-4 pb-3 pt-2 text-xs text-muted">{note}</p>
        )}
      </div>
    </Widget>
  );
}

export const Component = StackedCategoryBarsComponent;

const widgetModule: WidgetModule<StackedCategoryBarsConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
